import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import ZephyrIconWhite from "@/Images/Icons/Zephyr_Icon_White.png";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocalPharmacyOutlinedIcon from "@mui/icons-material/LocalPharmacyOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

const Z_PURPLE = "#38184f";
const Z_BLUE = "#2baede";

const fmt = (v) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(v ?? 0));

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: 2,
        p: 2.5,
        border: "1px solid #e2e8f0",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "10px",
            background: `${color}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon sx={{ fontSize: 20, color }} />
        </Box>
        <Typography variant="body2" color="text.secondary" fontWeight={600} fontSize="0.82rem">
          {label}
        </Typography>
      </Box>
      <Typography fontWeight={800} fontSize="1.4rem" sx={{ color: "#1a1a1a" }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="body2" color="text.secondary" fontSize="0.78rem">
          {sub}
        </Typography>
      )}
    </Box>
  );
}

export default function ZephyrOverviewPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    phappsRequest(API_PATHS.ZEPHYR_ADMIN_OVERVIEW, "GET")
      .then((res) => setStats(res?.data ?? null))
      .catch(() => setError("Failed to load overview stats."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 4 }}>
      <CircularProgress sx={{ color: Z_PURPLE }} />
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>
  );

  const nextDate = stats?.nextStatementDate
    ? new Date(stats.nextStatementDate).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })
    : "—";

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5,
          background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src={ZephyrIconWhite} alt="Zephyr Claims" style={{ height: 20, objectFit: "contain" }} />
        </Box>
        <Typography fontWeight={800} fontSize="1.25rem" sx={{ color: Z_PURPLE }}>
          Overview
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" }, gap: 2 }}>
        <StatCard
          icon={AccountBalanceWalletOutlinedIcon}
          label="Outstanding Invoices"
          value={fmt(stats?.outstandingTotal)}
          sub="Scripts with no payment and not written off"
          color="#dc2626"
        />
        <StatCard
          icon={CheckCircleOutlineIcon}
          label="Reconciled This Period"
          value={fmt(stats?.reconciledThisPeriod?.total)}
          sub={`${stats?.reconciledThisPeriod?.count ?? 0} scripts — awaiting statement`}
          color="#16a34a"
        />
        <StatCard
          icon={PendingActionsIcon}
          label="Pending Scripts"
          value={fmt(stats?.pendingTotal)}
          sub="On hold, not written off, unpaid"
          color="#d97706"
        />
        <StatCard
          icon={CalendarTodayOutlinedIcon}
          label="Next Statement Date"
          value={nextDate}
          sub="Statements run on the 1st and 15th"
          color={Z_BLUE}
        />
        <StatCard
          icon={LocalPharmacyOutlinedIcon}
          label="Chemists This Round"
          value={String(stats?.distinctChemistCount ?? 0)}
          sub="Distinct chemists with reconciled, unstatement scripts"
          color={Z_PURPLE}
        />
        <StatCard
          icon={WarningAmberOutlinedIcon}
          label="Overdue Outstanding"
          value={fmt(stats?.overdueTotal)}
          sub="Invoices 30+ days old with no future recheck date"
          color="#b45309"
        />
      </Box>
    </Box>
  );
}
